import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { useTranslation } from 'react-i18next';

import { TOKENS } from '../worktimeLib';

/**
 * P-wait (§7.2): the order moved to `waiting` while a WO clock was running and the org has
 * auto_pause_on_waiting OFF. Offer to pause the WO clock(s) (day clock keeps running) or keep
 * running. The "do this automatically next time" checkbox writes config (admin-only) and is
 * hidden otherwise. Reminder: when auto-pause is ON this dialog never shows — the backend pauses
 * on commit and the session vanishes on the next poll.
 */
export default function PWaitDialog({ open, onPause, onKeep, canSetAuto, busy }) {
  const { t } = useTranslation();
  const [auto, setAuto] = useState(false);
  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: TOKENS.amberSoft,
            color: TOKENS.amber,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PauseCircleOutlineIcon fontSize="small" />
        </Box>
        {t('worktime.wait.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t('worktime.wait.body')}</DialogContentText>
        {canSetAuto && (
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={<Checkbox checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
            label={t('worktime.wait.rememberChoice')}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
        <Button
          onClick={() => onPause(auto)}
          variant="contained"
          disabled={busy}
          fullWidth
          sx={{ backgroundColor: TOKENS.green, '&:hover': { backgroundColor: TOKENS.greenDark } }}
        >
          {t('worktime.wait.pause')}
        </Button>
        <Button onClick={() => onKeep(auto)} variant="outlined" disabled={busy} fullWidth>
          {t('worktime.wait.keepRunning')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
